import { getAssessmentReportPurposeLabel } from './assessmentReportCatalogs';
import type {
  AssessmentReportEditorForm,
  AssessmentReportPayloadV2,
  AssessmentReportPurposeContextV2,
} from './assessmentReportDomain';
import {
  ASSESSMENT_REPORT_LEGACY_PURPOSE_PREFIX,
  buildLegacyAssessmentReportNarrativeSuggestions,
} from './assessmentReportSuggestions';

const sameText = (left: string | null | undefined, right: string | null | undefined): boolean =>
  (left ?? '').replace(/\s+/g, ' ').trim() === (right ?? '').replace(/\s+/g, ' ').trim();

const withoutLegacyText = (
  value: string | null,
  ...legacyValues: (string | null)[]
): string | null => legacyValues.some((legacy) => sameText(value, legacy)) ? null : value;

const withoutLegacyList = (value: string[], legacy: string[]): string[] =>
  value.filter((item) => !legacy.some((suggestion) => sameText(item, suggestion)));

/** Finalidade é um rótulo curto; descrições autorais ficam fora da identificação. */
export const getAssessmentReportPurposeText = (purpose: AssessmentReportPurposeContextV2): string =>
  purpose.type === 'other' && purpose.description.trim()
    ? purpose.description.trim()
    : getAssessmentReportPurposeLabel(purpose.type);

/**
 * Retira somente sugestões reconhecíveis das versões anteriores.
 * Não altera fontes, diagnósticos, autoria, assinatura ou o objeto persistido.
 * Textos alterados pelo profissional são preservados integralmente.
 */
export const getAssessmentReportPresentation = <
  T extends AssessmentReportEditorForm | AssessmentReportPayloadV2,
>(report: T): T => {
  const legacy = buildLegacyAssessmentReportNarrativeSuggestions(report);
  const emptyLegacy = buildLegacyAssessmentReportNarrativeSuggestions({ ...report, sources: [] });
  // A finalidade pode ter sido alterada depois de o texto automático ser gerado.
  const purposeSuffix = legacy.purposeDescription.slice(
    ASSESSMENT_REPORT_LEGACY_PURPOSE_PREFIX[report.purpose.type].length,
  );
  const legacyPurposeDescriptions = Object.values(ASSESSMENT_REPORT_LEGACY_PURPOSE_PREFIX)
    .flatMap((prefix) => [prefix, `${prefix}${purposeSuffix}`]);
  const purpose = {
    ...report.purpose,
    description: withoutLegacyText(
      report.purpose.description,
      ...legacyPurposeDescriptions,
    ) ?? '',
  };
  const fallbackNote = 'Não há ponto de corte rastreável aplicável; o resultado foi incluído sem classificação automática.';
  const sources = report.sources.map((source) => {
    const originalText = source.finding.conciseSummary;
    let text = originalText?.trim() ?? '';
    const sourceBoilerplate = [
      fallbackNote,
      'A avaliação de origem não oferece classificação automática segura para este relatório.',
      ...(source.snapshot.referenceText ? [
        `A avaliação de origem registrou a classificação “${source.snapshot.referenceText}”, apresentada de forma informativa e sujeita à correlação clínica.`,
      ] : []),
    ];
    // O fallback de versões antigas pode ter acrescentado mais de uma frase.
    let previousText: string;
    do {
      previousText = text;
      for (const sentence of sourceBoilerplate) {
        if (text.endsWith(sentence)) text = text.slice(0, -sentence.length).trim();
      }
    } while (text !== previousText);
    if (text === (originalText?.trim() ?? '')) return source;
    return {
      ...source,
      finding: {
        ...source.finding,
        conciseSummary: text || null,
      },
    };
  });

  if (report.mode === 'assessment_summary') {
    return {
      ...report,
      purpose,
      sources,
      content: {
        ...report.content,
        professionalOverview: withoutLegacyText(
          report.content.professionalOverview,
          legacy.summary.professionalOverview,
          emptyLegacy.summary.professionalOverview,
        ) ?? '',
        followUpGuidance: withoutLegacyText(report.content.followUpGuidance, legacy.summary.followUpGuidance),
        observations: withoutLegacyList(report.content.observations, legacy.summary.observations),
      },
    } as T;
  }

  return {
    ...report,
    purpose,
    sources,
    content: {
      ...report.content,
      clinicalContext: withoutLegacyText(
        report.content.clinicalContext,
        legacy.technical.clinicalContext,
        emptyLegacy.technical.clinicalContext,
      ) ?? '',
      integratedAnalysis: withoutLegacyText(
        report.content.integratedAnalysis,
        legacy.technical.integratedAnalysis,
        emptyLegacy.technical.integratedAnalysis,
      ) ?? '',
      physiotherapyConclusion: withoutLegacyText(
        report.content.physiotherapyConclusion,
        legacy.technical.physiotherapyConclusion,
        emptyLegacy.technical.physiotherapyConclusion,
      ) ?? '',
      recommendations: withoutLegacyList(report.content.recommendations, legacy.technical.recommendations),
      limitations: withoutLegacyText(report.content.limitations, legacy.technical.limitations),
    },
  } as T;
};
